import type { FeatureFlagKey } from '@vizit/constants';
import { useLDClient, withLDProvider } from 'launchdarkly-react-client-sdk';
import type { LDClient, LDFlagSet } from 'launchdarkly-react-client-sdk';
import * as React from 'react';
import { DEFAULT_FLAGS } from '../default-flags';
import { flagOverrides, initializeOverrideInterface } from '../overrides/overrides';
import { setFeatureFlagClient } from '../utils/require-feature-flags';

interface FeatureFlagContextValue {
  getFlag: <T = boolean>(flagKey: FeatureFlagKey, defaultValue: T) => T;
  ldClient?: LDClient;
  allFlags: LDFlagSet;
}

export const FeatureFlagContext = React.createContext<FeatureFlagContextValue | null>(null);

interface FeatureFlagProviderProps {
  children: React.ReactNode;
  defaultFlags?: Partial<Record<FeatureFlagKey, boolean | string | number>>;
}

/**
 * Simple feature flag provider that wraps LaunchDarkly
 */
function FeatureFlagProviderInner({ children }: { children?: React.ReactNode }) {
  const ldClient = useLDClient();
  const [allFlags, setAllFlags] = React.useState<LDFlagSet>(ldClient?.allFlags() || {});

  React.useEffect(() => {
    initializeOverrideInterface(ldClient);
  }, [ldClient]);

  React.useEffect(() => {
    if (!ldClient) return;

    const handleChange = () => {
      setAllFlags(ldClient.allFlags());
    };

    ldClient.on('change', handleChange);
    return () => ldClient.off('change', handleChange);
  }, [ldClient]);

  const getFlag = React.useCallback(
    <T,>(flagKey: FeatureFlagKey, defaultValue: T): T => {
      // Check overrides first
      if (flagKey in flagOverrides) {
        return flagOverrides[flagKey] as T;
      }

      // Fall back to LaunchDarkly or default
      if (ldClient) {
        return ldClient.variation(flagKey, DEFAULT_FLAGS[flagKey] ?? defaultValue) as T;
      }

      return defaultValue;
    },
    [ldClient],
  );

  const contextValue = React.useMemo(
    () => ({
      getFlag,
      ldClient,
      allFlags,
    }),
    [getFlag, ldClient, allFlags],
  );

  React.useEffect(() => {
    if (ldClient) {
      setFeatureFlagClient(ldClient, contextValue);
    }
  }, [ldClient, contextValue]);

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
}

/**
 * Create the LaunchDarkly provider with config
 */
export function createFeatureFlagProvider(config: {
  clientSideId: string;
  user?: {
    key: string;
    email?: string;
    organizationId?: string;
    portfolioId?: string;
  };
}): React.ComponentType<{ children: React.ReactNode }> {
  const { clientSideId, user = { key: 'anonymous' } } = config;

  const LDProvider = withLDProvider({
    clientSideID: clientSideId,
    context: {
      kind: 'user',
      key: user.key,
      email: user.email,
      organizationId: user.organizationId,
      portfolioId: user.portfolioId,
      _meta: {
        privateAttributes: ['/email'],
      },
    },
    options: {
      streaming: true,
    },
    timeout: 2,
  })(FeatureFlagProviderInner);

  // Type assertion to fix LaunchDarkly's type inference
  return LDProvider as unknown as React.ComponentType<{ children: React.ReactNode }>;
}

// Fallback provider when LaunchDarkly is not configured
export function FeatureFlagFallbackProvider({ children, defaultFlags = DEFAULT_FLAGS }: FeatureFlagProviderProps) {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  React.useEffect(() => {
    // Initialize override interface on mount
    initializeOverrideInterface();

    // Listen for override changes
    const handleOverrideChange = () => {
      forceUpdate();
    };

    window.addEventListener('flag-override-changed', handleOverrideChange);
    return () => window.removeEventListener('flag-override-changed', handleOverrideChange);
  }, []);

  const getFlag = React.useCallback(
    <T,>(flagKey: string, defaultValue: T): T => {
      // Check overrides first
      if (flagKey in flagOverrides) {
        return flagOverrides[flagKey] as T;
      }

      // Use default flags if available
      if (flagKey in defaultFlags) {
        return defaultFlags[flagKey as keyof typeof DEFAULT_FLAGS] as T;
      }

      // Fall back to provided default
      return defaultValue;
    },
    [defaultFlags],
  );

  const contextValue = React.useMemo(
    () => ({
      getFlag,
      ldClient: undefined,
      allFlags: DEFAULT_FLAGS as LDFlagSet,
    }),
    [getFlag],
  );

  return <FeatureFlagContext.Provider value={contextValue}>{children}</FeatureFlagContext.Provider>;
}

export type { FeatureFlagContextValue };
